import { useEffect, useMemo } from 'react'
import { FOOTER_TYPES, PRESCRIPTION_LAYOUTS } from 'src/consumer/constants'
import { EMR_SECTION_IDS } from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/emrSectionIds'
import PrintablePatientHistoryNotes from 'src/mentor/components/PrescriptionMakingPage/printComponents/printComponents/prescriptionPrintableItems/PrintablePatientHistoryNotes'
import { PrescriptionDrugAdviceTable } from '../../PrescriptionMakingPageComponents'
import {
  PmpConsultationBodyContentContainer,
  PmpConsultationContentContainer,
} from '../../PrescriptionMakingPageStyles'
import {
  checkAndRenderBasedOnEmrSections,
  EXCLUDED_ORDER_IDS_FROM_PRESC,
  PRESCRIPTION_ITEM_ORDER_IDS,
} from '../utils'
import PrescriptionPrintableFooter from './PrescriptionPrintableFooter'
import ContinuedToAndFromDiv from './prescriptionPrintableItems/ContinuedToAndFromDiv'
import PrintableAssessments from './prescriptionPrintableItems/PrintableAssessments'
import PrintableComplaints from './prescriptionPrintableItems/PrintableComplaints'
import PrintableDiagnosis from './prescriptionPrintableItems/PrintableDiagnosis'
import PrintableHomunculus from './prescriptionPrintableItems/PrintableHomunculus'
import PrintableInstructions from './prescriptionPrintableItems/PrintableInstructions'
import PrintableInvestigations from './prescriptionPrintableItems/PrintableInvestigations'
import PrintableNextVisitColumn from './prescriptionPrintableItems/PrintableNextVisitColumn'
import PrintableSignatureColumn from './prescriptionPrintableItems/PrintableSignatureColumn'
import PrintableObservations from './prescriptionPrintableItems/PrintableObservations'
import PrintablePrescriptionAdvice from './prescriptionPrintableItems/PrintablePrescriptionAdvice'
import PrintableReferredToDoctorName from './prescriptionPrintableItems/PrintableReferredToDoctorName'
import PrintableTherapyRecommended from './prescriptionPrintableItems/PrintableTherapyRecommended'
import PrintableVitals from './prescriptionPrintableItems/PrintableVitals'
import { PMPBodyDebugHighlightDiv } from './prescriptionPrintableItems/styledComponent'
import { getValidDiagnosisArrayForPrescription } from './prescriptionPrintableItems/util'
import FreeFormRichTextView from 'src/mentor/components/EmrRelatedComponents/FreeFormRichText/FreeFormRichTextView'

export default function PrescriptionPrintableBody({
  value,
  setValue,
  placeholder,
  alsoChangePropValue,
  setPropValue,
  selectedTherapies,
  selectedAdvice,
  nextVisitInputType,
  nextVisitInputValue,
  prescriptionData,
  selectedObservations,
  selectedComplaints,
  selectedDiagnosis,
  selectedAssessments,
  referredToDoctorName,
  finalRequestedTestsBy,

  drugsAdvice,
  selectedInstructions,
  suggestedBookingTherapy,
  selectedInvestigations,
  jointHomunculusData,
  prescriptionViewSettings,
  vital,
  debug = false,
  printType,
  debugHighlightStates,
  footerHeight,
  currentPage,
  pageDetailsObject,
  leftSidebar,
  rightSidebar,
  leftSidebarWidth = 0,
  rightSidebarWidth = 0,
  totalPages,
  emrSections,
  footerType,
  styleOverrides,
  isLastPage = false,
  patientHistoryNotes,
  patientHistoryNotesRef,
  signatureRef,
  useFullUrlForFooterImages,
  colorMode = 'colored',
  getSectionColor,
  A4Height,
  headerHeight,
  allPagesData,
  useEmptyBox = false,
  practitionerId, // Optional: required for support portal
  freeFormRichText,
  freeFormSection,
}) {
  var layoutStyles

  const pageDetailsItems = pageDetailsObject?.items

  // Print-footer field toggles drive two cross-cutting render behaviours:
  //  - showName: the practitioner name moves into the footer strip, so the separate
  //    signature panel is suppressed here.
  //  - showPageNumber: "Page X of Y" prints in the footer strip, so the
  //    "Continued"/"Continuing" continuation markers/boxes are suppressed.
  const printFooterSettings = prescriptionData?.footer?.printFooterSettings || {}
  const hideSignaturePanel = Boolean(printFooterSettings.showName)
  const usePageNumberFooter = Boolean(printFooterSettings.showPageNumber)

  var lenOfData = useMemo(() => {
    var len = 0
    if (Array.isArray(selectedObservations) && selectedObservations?.length > 0) {
      len += 1
    }
    if (Array.isArray(selectedComplaints) && selectedComplaints?.length > 0) {
      len += 1
    }
    if (Array.isArray(selectedDiagnosis) && selectedDiagnosis?.length > 0) {
      len += 1
    }
    return len || 1
  }, [selectedObservations, selectedComplaints, selectedDiagnosis])

  switch (prescriptionData?.layout) {
    case PRESCRIPTION_LAYOUTS.largerFontLayout:
      layoutStyles = {
        display: 'grid',
        gridTemplateColumns: `repeat(${lenOfData}, 1fr)`,
        width: '100%',
      }
      break
    default:
      layoutStyles = {}
      break
  }

  function checkIfItemIsOnCurrentPage(orderId) {
    if (
      !EXCLUDED_ORDER_IDS_FROM_PRESC.includes(orderId) &&
      !checkAndRenderBasedOnEmrSections(orderId, emrSections)
    ) {
      return false
    }
    // Check for exact match
    if (pageDetailsItems?.includes(orderId)) {
      return true
    }
    // Also check if this is a drug_advice chunk (drug_advice_chunk_0, drug_advice_chunk_1, etc.)
    if (orderId === 'drug_advice') {
      return pageDetailsItems?.some(item => item?.startsWith('drug_advice_chunk_'))
    }
    // Also check if this is a free-form rich text chunk
    if (orderId === 'free_form_rich_text') {
      return pageDetailsItems?.some(item => item?.startsWith('free_form_rich_text_chunk_') || item === 'free_form_rich_text')
    }
    return false
  }
  
  // Returns the free-form chunk metadata for the current page, or null if free-form is inline (no chunking)
  function getFreeFormChunkMetadata() {
    if (!pageDetailsItems) return null
    const hasChunk = pageDetailsItems.some(item => item?.startsWith('free_form_rich_text_chunk_'))
    if (!hasChunk) return null
    return pageDetailsObject?.freeFormChunkMetadata ?? null
  }
  
  // Helper to get chunk index and data from page items
  function getDrugAdviceChunkFromPage() {
    if (!pageDetailsItems) return null
    const chunkItem = pageDetailsItems.find(item => item?.startsWith('drug_advice_chunk_'))
    if (!chunkItem) return null
    const match = chunkItem.match(/drug_advice_chunk_(\d+)/)
    if (!match) return null
    const chunkIndex = parseInt(match[1], 10)
    
    // CRITICAL: Try to get the resized chunk data from pageDetailsObject first
    // This contains the actual resized chunk data from the page break calculation
    if (pageDetailsObject?.chunkMetadata?.chunkData && Array.isArray(pageDetailsObject.chunkMetadata.chunkData)) {
      const chunk = pageDetailsObject.chunkMetadata.chunkData
      
      // Use stored startRow and endRow if available, otherwise calculate
      let startRow = pageDetailsObject.chunkMetadata.startRow
      let endRow = pageDetailsObject.chunkMetadata.endRow
      
      if (!startRow || !endRow) {
        // Fallback: Calculate start and end row numbers based on all previous chunks
        startRow = 1
        if (allPagesData && Array.isArray(allPagesData)) {
          let totalRowsBefore = 0
          for (let i = 0; i < currentPage - 1; i++) {
            const prevPageData = allPagesData[i]
            if (prevPageData?.chunkMetadata?.chunkData) {
              totalRowsBefore += prevPageData.chunkMetadata.chunkData.length
            }
          }
          startRow = totalRowsBefore + 1
        }
        endRow = startRow + chunk.length - 1
      }
      
      // Debug logging (disabled to reduce console noise)
      // Uncomment if debugging chunking issues:
      // if (debug) {
      //   console.log('[getDrugAdviceChunkFromPage] Using stored', {
      //     chunkIdx: chunkIndex,
      //     page: currentPage,
      //     rows: chunk.length,
      //   })
      // }
      
      return { chunk, chunkIndex, startRow, endRow }
    }
    
    // Debug: Log when fallback is used (disabled to reduce console noise)
    // Uncomment if debugging chunking issues:
    // if (debug) {
    //   console.log('[getDrugAdviceChunkFromPage] Using fallback', {
    //     chunkIdx: chunkIndex,
    //     page: currentPage,
    //   })
    // }
    
    // Fallback: Calculate from drugsAdvice if chunk data not available
    const filteredDrugsAdvice = drugsAdvice?.filter((item) => item?.drug?.label) || []
    if (filteredDrugsAdvice.length === 0) return null
    
    // Use the same calculation as splitItemsIntoChunks
    const estimatedRowHeight = 55
    const headerRowHeight = 50
    const otherContentHeight = 100
    const padding = 20
    const footerHeightToUse = footerHeight || 50 // Match default in splitItemsIntoChunks
    const availableHeight = (A4Height || 1123) - 
                            (headerHeight || 0) -
                            footerHeightToUse - 
                            otherContentHeight
    const rowsPerPage = Math.max(1, Math.floor((availableHeight - headerRowHeight - padding) / estimatedRowHeight))
    
    // Debug logging (disabled to reduce console noise)
    // if (debug) {
    //   console.log('[getDrugAdviceChunkFromPage]', {
    //     chunkIndex,
    //     rowsPerPage,
    //     availableHeight,
    //     footerHeightToUse,
    //     startIndex: chunkIndex * rowsPerPage,
    //     totalDrugs: filteredDrugsAdvice.length
    //   })
    // }
    
    const startIndex = chunkIndex * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    const chunk = filteredDrugsAdvice.slice(startIndex, endIndex)
    const startRow = startIndex + 1
    const endRow = startIndex + chunk.length
    
    return { chunk, chunkIndex, startRow, endRow }
  }

  // Estimate row height: header (~50px) + each row (~50-60px including padding and notes sub-row)
  // Use a conservative estimate of 55px per row (including header)
  const estimatedRowHeight = 55
  const headerRowHeight = 50
  
  // Calculate available height for Rx table on a page
  // A4Height - headerHeight - footerHeight (if footer is on this page) - other content
  // For continuation pages, we have less space due to "Continued from" div
  const isContinuationPage = checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom)
  const continuedFromHeight = isContinuationPage ? 30 : 0
  const otherContentHeight = 100 // Estimate for other sections that might be on the page
  // Check if footer is on current page - if so, we need to reserve space for it
  const isFooterOnCurrentPage = checkIfItemIsOnCurrentPage('prescriptionFooter')
  const footerHeightOnThisPage = isFooterOnCurrentPage ? (footerHeight || 0) : 0
  const availableHeight = (A4Height || 1123) - 
                          (headerHeight || 0) -
                          footerHeightOnThisPage - 
                          otherContentHeight - 
                          continuedFromHeight
  
  // Calculate how many rows fit per page (excluding header) - try to fit as much as possible
  // Add some padding to ensure we don't overflow
  const rowsPerPage = Math.max(1, Math.floor((availableHeight - headerRowHeight - 20) / estimatedRowHeight))
  
  // Debug logging (disabled to reduce console noise)
  // if (debug) {
  //   console.log('[Rx Chunk Calculation]', {
  //     A4Height,
  //     headerHeight,
  //     footerHeight,
  //     footerHeightOnThisPage,
  //     isFooterOnCurrentPage,
  //     availableHeight,
  //     rowsPerPage,
  //     totalDrugs: drugsAdvice?.filter((item) => item?.drug?.label)?.length,
  //     estimatedChunks: Math.ceil((drugsAdvice?.filter((item) => item?.drug?.label)?.length || 0) / rowsPerPage)
  //   })
  // }

  // Calculate Rx table chunks for page breaking
  const rxTableChunks = useMemo(() => {
    if (!drugsAdvice || !Array.isArray(drugsAdvice) || drugsAdvice.length === 0) {
      return []
    }
    
    const filteredDrugsAdvice = drugsAdvice.filter((item) => item?.drug?.label)
    if (filteredDrugsAdvice.length === 0) {
      return []
    }
    
    // Split into chunks
    const chunks = []
    for (let i = 0; i < filteredDrugsAdvice.length; i += rowsPerPage) {
      chunks.push(filteredDrugsAdvice.slice(i, i + rowsPerPage))
    }
    
    return chunks
  }, [drugsAdvice, rowsPerPage])

  // Determine which chunk to show on current page by counting previous pages with drug_advice
  const rxChunkForCurrentPage = useMemo(() => {
    if (rxTableChunks.length === 0) return null
    
    const isDrugAdviceOnPage = checkIfItemIsOnCurrentPage('drug_advice')
    if (!isDrugAdviceOnPage) return null
    
    // Count how many previous pages (before currentPage) had drug_advice or drug_advice chunks
    let pagesWithDrugAdviceBeforeCurrent = 0
    if (allPagesData && Array.isArray(allPagesData)) {
      for (let i = 0; i < currentPage - 1; i++) {
        const pageData = allPagesData[i]
        // Check for both 'drug_advice' and 'drug_advice_chunk_*' items
        const hasDrugAdvice = pageData?.items?.includes('drug_advice') || 
                             pageData?.items?.some(item => item?.startsWith('drug_advice_chunk_'))
        if (hasDrugAdvice) {
          pagesWithDrugAdviceBeforeCurrent++
        }
      }
    }
    
    // The chunk index is the number of previous pages with drug_advice
    // This works because each page with drug_advice shows one chunk
    // However, we also need to check if this is a continuation page
    const isContinuation = checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom)
    
    // If it's a continuation, we're showing a chunk after the first one
    // The chunk index should be the number of previous pages with drug_advice
    // But we also need to account for the fact that the first page might not be a continuation
    let chunkIndex = pagesWithDrugAdviceBeforeCurrent
    
    // If this is the first page with drug_advice (not a continuation), show chunk 0
    // If this is a continuation page, show the next chunk
    if (!isContinuation && pagesWithDrugAdviceBeforeCurrent === 0) {
      chunkIndex = 0
    } else if (isContinuation) {
      // On continuation pages, chunk index = number of previous pages with drug_advice
      chunkIndex = pagesWithDrugAdviceBeforeCurrent
    }
    
    // Debug logging (disabled to reduce console noise)
    // Uncomment if debugging chunking issues:
    // console.log('[Rx Chunk Debug]', {
    //   currentPage,
    //   isContinuation,
    //   pagesWithDrugAdviceBeforeCurrent,
    //   chunkIndex,
    //   totalChunks: rxTableChunks.length,
    //   chunkSizes: rxTableChunks.map(c => c.length),
    //   pageDetailsItems,
    //   allPagesData: allPagesData?.map((p, idx) => ({
    //     page: idx + 1,
    //     hasDrugAdvice: p?.items?.includes('drug_advice'),
    //     hasDrugAdviceChunk: p?.items?.some(item => item?.startsWith('drug_advice_chunk_')),
    //     hasContinuedFrom: p?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom),
    //     hasChunkMetadata: !!p?.chunkMetadata,
    //     chunkMetadata: p?.chunkMetadata ? {
    //       chunkIndex: p.chunkMetadata.chunkIndex,
    //       hasChunkData: !!p.chunkMetadata.chunkData,
    //       chunkDataLength: p.chunkMetadata.chunkData?.length
    //     } : null,
    //     items: p?.items
    //   }))
    // })
    
    if (chunkIndex >= rxTableChunks.length) {
      // Fallback: show last chunk if index is out of bounds
      const lastChunkIndex = rxTableChunks.length - 1
      const lastChunk = rxTableChunks[lastChunkIndex]
      const totalRows = rxTableChunks.flat().length
      return { 
        chunk: lastChunk, 
        chunkIndex: lastChunkIndex,
        startRow: totalRows - lastChunk.length + 1,
        endRow: totalRows
      }
    }
    
    const chunk = rxTableChunks[chunkIndex]
    // Calculate start and end row numbers (1-indexed)
    const startRow = chunkIndex * rowsPerPage + 1
    const endRow = startRow + chunk.length - 1
    
    return { chunk, chunkIndex, startRow, endRow }
  }, [rxTableChunks, pageDetailsItems, currentPage, allPagesData, rowsPerPage, debug])

  useEffect(() => {
    if (alsoChangePropValue) {
      setPropValue(value)
    }
  }, [value])

  return (
    <>
      {checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.continuedFrom) && !usePageNumberFooter && (
        <ContinuedToAndFromDiv currentText={`Continued ${currentPage}/${totalPages}`} />
      )}
      {/* {checkIfItemIsOnCurrentPage('patientDetails') && (
        <PatientDetails
          debug={debug}
          prescriptionData={prescriptionData}
          printType={printType}
          debugHighlightStates={debugHighlightStates}
          prescriptionViewSettings={prescriptionViewSettings}
        />
      )} */}
      {checkIfItemIsOnCurrentPage('patient-vitals') && (
        <PrintableVitals
          debug={debug}
          vital={vital}
          printType={printType}
          debugHighlightStates={debugHighlightStates}
          styleOverrides={styleOverrides?.[EMR_SECTION_IDS.PATIENT_VITALS]}
          practitionerId={practitionerId}
          vitalsDisplayFormat={prescriptionData?.body?.vitalsDisplayFormat}
          vitalsNameBold={prescriptionData?.body?.vitalsNameBold}
          listItemSeparator={prescriptionData?.body?.listItemSeparator}
        />
      )}
      <div
        className={`d-flex justify-content-center ${
          footerType !== FOOTER_TYPES.inLineAtEnd ? 'flex-grow-1' : isLastPage ? '' : 'flex-grow-1'
        }`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          width: '100%',
        }}
      >
        <div
          className="d-flex justify-content-center"
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {leftSidebar && <div style={{ width: `${leftSidebarWidth}px`, flexShrink: 0 }}></div>}
          <PmpConsultationBodyContentContainer
            // headerHeight={headerDimensions?.height}
            // footerHeight={footerDimensions?.height}
            debug={debug}
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
            data-prescription-body="true"
          >
            {debugHighlightStates?.prescriptionBody && (
              <PMPBodyDebugHighlightDiv
                id="debug-body"
                debugHighlight={debugHighlightStates?.prescriptionBody}
              ></PMPBodyDebugHighlightDiv>
            )}
            {/* for calculating the height of the items inside */}

            <div style={{ 
              flex: 1, 
              minHeight: 0, 
              display: 'flex', 
              flexDirection: 'column',
              position: 'relative',
              // Allow patient history notes to flow freely
              ...(checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes) ? {
                height: 'auto',
                minHeight: 'auto',
                flex: 'none',
              } : {})
            }}>
              <PmpConsultationContentContainer 
                debug={debug} 
                style={{ 
                  flex: checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes) 
                    ? 'none' 
                    : (checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo) ? '0 1 auto' : 1), 
                  minHeight: checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes) ? 'auto' : 0,
                  // Prevent content from overlapping with the box
                  ...(checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo) ? {
                    flexShrink: 1,
                    overflow: 'visible',
                  } : {})
                }}
              >
                <div className="d-flex flex-column">
                  <div
                    style={{
                      width: '100%',
                    }}
                  >
                    <div style={layoutStyles}>
                      {Array.isArray(selectedObservations) &&
                        selectedObservations?.length > 0 &&
                        checkIfItemIsOnCurrentPage('observations') && (
                          <PrintableObservations
                            debug={debug}
                            selectedObservations={selectedObservations}
                            debugHighlightStates={debugHighlightStates}
                            styleOverrides={styleOverrides?.[EMR_SECTION_IDS.OBSERVATIONS]}
                            listItemSeparator={prescriptionData?.body?.listItemSeparator}
                          />
                        )}
                      {Array.isArray(selectedComplaints) &&
                        selectedComplaints?.length > 0 &&
                        checkIfItemIsOnCurrentPage('health_complaints') && (
                          <PrintableComplaints
                            debug={debug}
                            selectedComplaints={selectedComplaints}
                            debugHighlightStates={debugHighlightStates}
                            styleOverrides={styleOverrides?.[EMR_SECTION_IDS.HEALTH_COMPLAINTS]}
                            listItemSeparator={prescriptionData?.body?.listItemSeparator}
                          />
                        )}
                      {Array.isArray(getValidDiagnosisArrayForPrescription(selectedDiagnosis)) &&
                        getValidDiagnosisArrayForPrescription(selectedDiagnosis)?.length > 0 &&
                        checkIfItemIsOnCurrentPage('diagnosis') && (
                          <PrintableDiagnosis
                            debug={debug}
                            selectedDiagnosis={selectedDiagnosis}
                            debugHighlightStates={debugHighlightStates}
                            styleOverrides={styleOverrides?.[EMR_SECTION_IDS.DIAGNOSIS]}
                            listItemSeparator={prescriptionData?.body?.listItemSeparator}
                          />
                        )}
                    </div>
                  </div>
                  {checkIfItemIsOnCurrentPage('drug_advice') && (() => {
                    // Try to get chunk from page break system first (new method)
                    const chunkFromPage = getDrugAdviceChunkFromPage()
                    // Fallback to old method if new method doesn't work
                    const chunkData = chunkFromPage || rxChunkForCurrentPage
                    
                    // Debug logging (disabled to reduce console noise)
                    // Uncomment if debugging chunking issues:
                    // if (debug || !chunkData) {
                    //   console.log('[Rendering Rx]', {
                    //     page: currentPage,
                    //     pageDetailsItems,
                    //     source: chunkFromPage ? 'page' : 'fallback',
                    //     chunkFromPage: chunkFromPage ? {
                    //       chunkIndex: chunkFromPage.chunkIndex,
                    //       rows: chunkFromPage.chunk?.length
                    //     } : null,
                    //     rxChunkForCurrentPage: rxChunkForCurrentPage ? {
                    //       chunkIndex: rxChunkForCurrentPage.chunkIndex,
                    //       rows: rxChunkForCurrentPage.chunk?.length
                    //     } : null,
                    //     chunkIdx: chunkData?.chunkIndex,
                    //     rows: chunkData?.chunk?.length,
                    //     hasChunkData: !!chunkData
                    //   })
                    // }
                    
                    if (!chunkData) return null
                    
                    return (
                      <PrescriptionDrugAdviceTable
                        prescriptionData={prescriptionData}
                        drugsAdvice={chunkData.chunk}
                        prescriptionViewSettings={prescriptionViewSettings}
                        debug={debug}
                        debugHighlightStates={debugHighlightStates}
                        drugAdviceSubSections={
                          emrSections?.find((item) => item?.id === 'drug_advice')?.subSections
                        }
                        styleOverrides={styleOverrides?.[EMR_SECTION_IDS.DRUG_ADVICE]}
                        colorMode={colorMode}
                        getSectionColor={getSectionColor}
                        isContinuation={chunkData.chunkIndex > 0}
                        chunkRange={chunkData.startRow && chunkData.endRow ? {
                          startRow: chunkData.startRow,
                          endRow: chunkData.endRow
                        } : null}
                        totalDrugsCount={drugsAdvice?.filter((item) => item?.drug?.label)?.length || 0}
                        allDrugsAdvice={drugsAdvice}
                      />
                    )
                  })()}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {Array.isArray(selectedAdvice) &&
                    selectedAdvice.length > 0 &&
                    checkIfItemIsOnCurrentPage('advice') && (
                      <PrintablePrescriptionAdvice
                        debug={debug}
                        selectedAdvice={selectedAdvice}
                        debugHighlightStates={debugHighlightStates}
                        styleOverrides={styleOverrides?.[EMR_SECTION_IDS.ADVICE]}
                        listItemSeparator={prescriptionData?.body?.listItemSeparator}
                      />
                    )}
                  {Array.isArray(selectedInvestigations) &&
                    selectedInvestigations.length > 0 &&
                    checkIfItemIsOnCurrentPage('investigations') && (
                      <PrintableInvestigations
                        debug={debug}
                        selectedInvestigations={selectedInvestigations}
                        finalRequestedTestsBy={finalRequestedTestsBy}
                        debugHighlightStates={debugHighlightStates}
                        styleOverrides={styleOverrides?.[EMR_SECTION_IDS.INVESTIGATIONS]}
                        listItemSeparator={prescriptionData?.body?.listItemSeparator}
                      />
                    )}
                  {Array.isArray(selectedAssessments) &&
                    selectedAssessments?.length > 0 &&
                    checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.assessments) && (
                      <PrintableAssessments
                        debug={debug}
                        debugHighlightStates={debugHighlightStates}
                        selectedAssessments={selectedAssessments}
                        styleOverrides={styleOverrides?.[EMR_SECTION_IDS.PSYCH_ASSESSMENTS]}
                        listItemSeparator={prescriptionData?.body?.listItemSeparator}
                      />
                    )}
                  {/* Psychotherapy Recommended with Suggested Therapies - no border/rectangle */}
                  {(suggestedBookingTherapy && checkIfItemIsOnCurrentPage('therapyRecommended')) && (
                    <PrintableTherapyRecommended
                      debug={debug}
                      debugHighlightStates={debugHighlightStates}
                      suggestedBookingTherapy={suggestedBookingTherapy}
                      selectedTherapies={Array.isArray(selectedTherapies) && selectedTherapies.length > 0 ? selectedTherapies : null}
                      styleOverrides={styleOverrides?.[EMR_SECTION_IDS.THERAPY_RECOMMENDATION]}
                      listItemSeparator={prescriptionData?.body?.listItemSeparator}
                    />
                  )}
                  {checkIfItemIsOnCurrentPage('joint_homunculus') && (
                    <PrintableHomunculus
                      debug={debug}
                      jointHomunculusData={jointHomunculusData}
                      debugHighlightStates={debugHighlightStates}
                      styleOverrides={styleOverrides?.[EMR_SECTION_IDS.JOINT_HOMUNCULUS]}
                    />
                  )}
                  {Array.isArray(selectedInstructions) &&
                    selectedInstructions.length > 0 &&
                    checkIfItemIsOnCurrentPage('instructions') && (
                      <PrintableInstructions
                        debug={debug}
                        debugHighlightStates={debugHighlightStates}
                        selectedInstructions={selectedInstructions}
                        styleOverrides={styleOverrides?.[EMR_SECTION_IDS.INSTRUCTIONS]}
                        listItemSeparator={prescriptionData?.body?.listItemSeparator}
                      />
                    )}
                  {checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.refer_to) && (
                    <PrintableReferredToDoctorName
                      debug={debug}
                      debugHighlightStates={debugHighlightStates}
                      referredToDoctorName={referredToDoctorName}
                      prescriptionData={prescriptionData}
                      styleOverrides={styleOverrides?.[EMR_SECTION_IDS.REFER_TO]}
                    />
                  )}
                  {(() => {
                    // Render next_visit_details and free_form_rich_text in the order
                    // defined by pageDetailsItems so the layout matches the EMR section config.
                    const freeFormIdxInPage = pageDetailsItems?.findIndex(item =>
                      item?.startsWith('free_form_rich_text_chunk_') || item === 'free_form_rich_text'
                    ) ?? -1
                    const nextVisitIdxInPage = pageDetailsItems?.findIndex(item => item === 'next_visit_details') ?? -1
                    const freeFormBeforeNextVisit =
                      freeFormIdxInPage !== -1 &&
                      (nextVisitIdxInPage === -1 || freeFormIdxInPage < nextVisitIdxInPage)

                    const nextVisitEl = checkIfItemIsOnCurrentPage('next_visit_details') ? (
                      <PrintableNextVisitColumn
                        debug={debug}
                        debugHighlightStates={debugHighlightStates}
                        nextVisitInputType={nextVisitInputType}
                        nextVisitInputValue={nextVisitInputValue}
                        prescriptionData={prescriptionData}
                        styleOverrides={styleOverrides?.[EMR_SECTION_IDS.NEXT_VISIT_DETAILS]}
                        prescriptionViewSettings={prescriptionViewSettings}
                      />
                    ) : null

                    const freeFormEl = checkIfItemIsOnCurrentPage(EMR_SECTION_IDS.FREE_FORM_RICH_TEXT) && freeFormRichText ? (() => {
                      const chunkMeta = getFreeFormChunkMetadata()
                      const printTitleEnabled = freeFormSection?.printTitleEnabled ?? freeFormSection?.emrSectionResponse?.section?.printTitleEnabled ?? false
                      const printTitle = freeFormSection?.printTitle ?? freeFormSection?.emrSectionResponse?.section?.printTitle ?? ''
                      const displayTitle = freeFormSection?.title ?? freeFormSection?.label ?? freeFormSection?.emrSectionResponse?.section?.title ?? 'Free-form Rich Text'

                      if (chunkMeta && chunkMeta.height > 0) {
                        return (
                          <div style={{ height: `${chunkMeta.height}px`, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: `-${chunkMeta.startY}px`, width: '100%' }}>
                              <FreeFormRichTextView
                                html={freeFormRichText}
                                printTitleEnabled={chunkMeta.chunkIndex === 0 ? printTitleEnabled : false}
                                printTitle={printTitle}
                                displayTitle={displayTitle}
                              />
                            </div>
                          </div>
                        )
                      }
                      return (
                        <FreeFormRichTextView
                          html={freeFormRichText}
                          printTitleEnabled={printTitleEnabled}
                          printTitle={printTitle}
                          displayTitle={displayTitle}
                        />
                      )
                    })() : null

                    return freeFormBeforeNextVisit ? (
                      <>{freeFormEl}{nextVisitEl}</>
                    ) : (
                      <>{nextVisitEl}{freeFormEl}</>
                    )
                  })()}
                  {checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.signature) && !hideSignaturePanel && (
                    <PrintableSignatureColumn
                      prescriptionData={prescriptionData}
                      emrSections={emrSections}
                      signatureRef={signatureRef}
                      styleOverrides={styleOverrides?.[EMR_SECTION_IDS.SIGNATURE]}
                      debug={debug}
                      debugHighlightStates={debugHighlightStates}
                    />
                  )}
                </div>
              </PmpConsultationContentContainer>
              {/* "Continued on next page" box - fills remaining space when there's significant blank space */}
              {/* Only show on pages that have continuedTo AND are not the last page */}
              {/* Ensure only one box per page - count how many times continuedTo appears and only render once */}
              {(() => {
                const continuedToCount = pageDetailsObject?.items?.filter(item => item === PRESCRIPTION_ITEM_ORDER_IDS.continuedTo).length || 0
                const shouldShowBox = !usePageNumberFooter &&
                                      checkIfItemIsOnCurrentPage(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo) &&
                                      !isLastPage &&
                                      pageDetailsObject?.remainingSpace !== undefined &&
                                      continuedToCount > 0
                
                // Only render if shouldShowBox is true (ensures only one box even if continuedTo appears multiple times)
                // Always render the component to show text, but showAsBox controls whether to show the box
                return shouldShowBox ? (
                  <ContinuedToAndFromDiv 
                    key={`continued-to-page-${currentPage}-space-${Math.round(pageDetailsObject?.remainingSpace || 0)}-idx-${pageDetailsObject?.items?.indexOf(PRESCRIPTION_ITEM_ORDER_IDS.continuedTo) || 0}`}
                    currentText={`Continuing ${currentPage}/${totalPages}`}
                    showAsBox={useEmptyBox}
                    remainingSpace={pageDetailsObject?.remainingSpace || 0}
                    pageNumber={currentPage}
                  />
                ) : null
              })()}
            </div>
          </PmpConsultationBodyContentContainer>
          {rightSidebar && <div style={{ width: `${rightSidebarWidth}px`, flexShrink: 0 }}></div>}
        </div>
        {/* Flexible spacer to push footer to bottom of A4 page - empty rectangle to fill space */}
        {checkIfItemIsOnCurrentPage('prescriptionFooter') && (
          <>
            <div 
              data-print-spacer="true" 
              className="footer-spacer-rectangle"
              style={{ 
                flex: '1 1 auto', 
                minHeight: 0,
                width: '100%',
                display: 'block',
                backgroundColor: 'transparent',
                border: 'none',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
              }}
              aria-hidden="true"
            />
            <PrescriptionPrintableFooter
              debugMode={debug}
              prescriptionData={prescriptionData}
              footerHeight={footerHeight}
              printType={printType}
              leaveSpace={false}
              styleOverrides={prescriptionViewSettings?.paperPrintSettings?.footerSettings?.printStyles}
              useFullUrlForFooterImages={useFullUrlForFooterImages}
              currentPage={currentPage}
              totalPages={totalPages}
            />
          </>
        )}
      </div>
    </>
  )
}
